import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import { format } from "date-fns";
import { useTranslation } from "react-i18next";
// @ts-ignore
import { useTheme } from "@mui/material/styles";
// helpers
import { navigateBackOrToMain } from "../../../../common/utils/helpers/navigateBackOrToMain";
import { shouldShowMilesAndMore } from "../../../../common/utils/transport/airlineBookingPayload";
// hooks
import { useActions } from "../../../../common/hooks/useActions";
// actions
import { munichCityPassActionCreators } from "../munichCityPassModule";
// components
import AppFormInput from "../../../../components/form/AppFormInputs/AppFormInput";
import AppButton from "../../../../components/ui/AppButton";
import MilesAndMoreInfo from "../../../../components/MilesAndMoreInfo";
import TransportCancellationPolicy from "../../../../components/TransportProviderContent/TransportCancellationPolicy";
import { type MunichCityPassBookingForm, type MunichCityPassOffer, type MunichCityPassPassenger } from "../types";

interface MunichCityPassSummaryProps {
  onBuyTicket: () => void;
  selectedOffer: MunichCityPassOffer | null;
  cancellationPolicy?: string | null;
}

export default function MunichCityPassSummary({
  onBuyTicket,
  selectedOffer,
  cancellationPolicy
}: MunichCityPassSummaryProps) {
  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const language = localStorage.getItem("i18nextLng") || "en";

  const tenantInfo = useSelector((state: any) => state.tenant.tenantInfo);
  const platformType = useSelector((state: any) => state.global.platformType);
  const prebookingInfoResponse = useSelector((state: any) => state.munichCityPass.prebookingInfoResponse);
  const passengerList = useSelector((state: any) => state.munichCityPass.passengerList) as MunichCityPassPassenger[];
  const dataForBooking = useSelector((state: any) => state.munichCityPass.dataForBooking) as MunichCityPassBookingForm;
  const errorField = useSelector((state: any) => state.errorField.errorField) as any;

  const resetMunichCityPassPrebookState = useActions(
    munichCityPassActionCreators.resetMunichCityPassPrebookStateAction,
    []
  );
  const updateBookingForm = useActions(munichCityPassActionCreators.updateBookingFormAction, []);

  const isAirline = platformType === "airline";

  const handleBookingFormInputChange = (e: any, field: keyof MunichCityPassBookingForm) => {
    updateBookingForm({
      ...dataForBooking,
      [field]: e.target.value
    });
  };

  const handleEditTicketOptionButton = () => {
    if (window.confirm(t("transport.editOptionText"))) {
      resetMunichCityPassPrebookState();
    }
  };

  const formatBirthDate = (birthDate: Date) => {
    if (!(birthDate instanceof Date) || Number.isNaN(birthDate.getTime())) {
      return "";
    }
    return format(birthDate, "dd.MM.yyyy");
  };

  return (
    prebookingInfoResponse && (
      <Box sx={{ display: "flex", flexDirection: "column" }} pb={3} pt={3}>
        <Box sx={{ display: "flex", flexDirection: "column", flex: 1 }}>
          <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, justifyContent: "space-between" }}>
            <Box>
              <Box className="summary-option-title" mb={2}>
                {t("transport.ticketOption")}
              </Box>
              <Box className="summary-option-title default-text-transform">{selectedOffer?.name}</Box>
            </Box>
            <Grid item sx={{ width: "auto", height: "40px" }} mt={{ xs: 1.5, sm: 0 }}>
              <AppButton
                label={t("transport.editBtn")}
                onClick={handleEditTicketOptionButton}
                disableRipple={true}
                customBackgroundColor={theme.palette.primary.dark}
                customHoverColor={theme.palette.primary.dark}
                fontSize="14px"
                dataAttribute="munich-city-pass-edit-option"
              />
            </Grid>
          </Box>

          <Grid container className="summary-passenger-box" mt={2} mb={2} pb={2} pt={2}>
            {passengerList?.length > 0 &&
              passengerList.map((passenger, index) => (
                <Box
                  key={index}
                  sx={{
                    display: "flex",
                    textAlign: "center",
                    alignItems: "center",
                    justifyContent: "center",
                    width: "100%"
                  }}
                >
                  <Grid item xs={3} pl={{ xs: 1, sm: 0 }}>
                    {passenger?.firstName} {passenger?.lastName}
                  </Grid>
                  <Grid item xs={3}>
                    {formatBirthDate(passenger?.birthDate)}
                  </Grid>
                  <Grid item xs={3}>
                    {prebookingInfoResponse?.details?.passengers?.[index]?.passenger_type || ""}
                  </Grid>
                  <Grid item xs={3}>
                    {prebookingInfoResponse?.details?.passengers?.[index]?.price || ""}{" "}
                    {prebookingInfoResponse?.details?.passengers?.[index]?.currency || ""}
                  </Grid>
                </Box>
              ))}
          </Grid>

          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Box sx={{ display: "flex", alignItems: "center" }} />
            <Box className="summary-total-price-label" mr={{ xs: 0, sm: 3, md: 6 }}>
              {t("transport.totalPrice")}: {prebookingInfoResponse?.formatted_price}
            </Box>
          </Box>
        </Box>

        <Grid container mt={4} spacing={2}>
          <Grid item xs={12} md={6}>
            <Grid item xs={12} mb={2} sx={{ flexDirection: "row", display: "flex", alignItems: "center" }}>
              <Grid item xs={3}>
                <Box sx={{ fontSize: { xs: 12, sm: 14 } }}>{t("transport.guestNameLabel")} *</Box>
              </Grid>
              <Grid item xs={9} ml={1}>
                <AppFormInput
                  name={"nameForm"}
                  type="string"
                  value={dataForBooking?.nameForm}
                  onChange={event => handleBookingFormInputChange(event, "nameForm")}
                  error={errorField?.nameForm}
                  dataAttribute="munich-city-pass-name"
                />
              </Grid>
            </Grid>

            <Grid item xs={12} mb={2} sx={{ flexDirection: "row", display: "flex", alignItems: "center" }}>
              <Grid item xs={3}>
                <Box sx={{ fontSize: { xs: 12, sm: 14 } }}>{t("transport.guestEmailLabel")} *</Box>
              </Grid>
              <Grid item xs={9} ml={1}>
                <AppFormInput
                  name={"emailForm"}
                  type="string"
                  value={dataForBooking?.emailForm}
                  onChange={event => handleBookingFormInputChange(event, "emailForm")}
                  error={errorField?.emailForm}
                  dataAttribute="munich-city-pass-email"
                />
              </Grid>
            </Grid>

            <Grid item xs={12} mb={2} sx={{ flexDirection: "row", display: "flex", alignItems: "center" }}>
              <Grid item xs={3}>
                <Box sx={{ fontSize: { xs: 12, sm: 14 } }}>{t("transport.commentLabel")}</Box>
              </Grid>
              <Grid item xs={9} ml={1}>
                <AppFormInput
                  name={"commentForm"}
                  type="string"
                  value={dataForBooking?.commentForm}
                  onChange={event => handleBookingFormInputChange(event, "commentForm")}
                  error={errorField?.commentForm}
                  dataAttribute="munich-city-pass-comment"
                />
              </Grid>
            </Grid>
          </Grid>

          {shouldShowMilesAndMore(prebookingInfoResponse?.miles_and_more, isAirline) && (
            <Grid item xs={12} md={6} pl={{ xs: 0, md: 5 }} pt={{ xs: 2, md: 0 }}>
              <MilesAndMoreInfo miles={prebookingInfoResponse?.miles_and_more} />
            </Grid>
          )}
        </Grid>

        {cancellationPolicy && (
          <Box pt={6}>
            <TransportCancellationPolicy cancellationPolicy={cancellationPolicy} />
          </Box>
        )}

        <Grid
          item
          xs={12}
          sx={{
            width: { xs: "100%", sm: "100%", md: isAirline ? "45%" : "30%" },
            margin: cancellationPolicy ? "24px auto 0 auto" : "60px auto 0 auto",
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            gap: { xs: 2, sm: 2 }
          }}
        >
          {isAirline && (
            <AppButton
              label={t("transport.goBack")}
              onClick={() => navigateBackOrToMain(navigate, { language })}
              disableRipple={true}
              customBackgroundColor={theme.palette.primary.dark}
              customHoverColor={theme.palette.primary.dark}
              fontSize="14px"
              dataAttribute="munich-city-pass-go-back-2"
            />
          )}
          <AppButton
            label={t("transport.buyTicketsBtn")}
            onClick={onBuyTicket}
            disableRipple={true}
            customBackgroundColor={tenantInfo?.style?.button?.color}
            customActiveColor={tenantInfo?.style?.button?.active}
            customHoverColor={tenantInfo?.style?.button?.hover}
            customClickColor={tenantInfo?.style?.button?.click}
            fontSize="14px"
            dataAttribute="munich-city-pass-buy-tickets"
          />
        </Grid>
      </Box>
    )
  );
}
